import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Editor } from '../components/NotionEditor';
import { apiService } from '../services/ApiService';
import type { OnePager } from '../types';

interface OnePagerEditorProps {
  templateOnePager?: OnePager;
  onRefresh?: () => void;
}

export const OnePagerEditor: React.FC<OnePagerEditorProps> = ({ templateOnePager, onRefresh }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!id);

  // Form state - for templates, prefix title with "Copy of " and don't make it a template by default
  const [title, setTitle] = useState(templateOnePager ? `Copy of ${templateOnePager.title}` : '');
  const [description, setDescription] = useState(templateOnePager?.description || '');
  const [content, setContent] = useState(templateOnePager?.content || '<p>Start writing your one-pager content here...</p>');
  const [isTemplate, setIsTemplate] = useState(false);

  useEffect(() => {
    if (id && !templateOnePager) {
      loadOnePager();
    }
  }, [id]);

  const loadOnePager = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const onePager = await apiService.getOnePagerById(id);
      setTitle(onePager.title);
      setDescription(onePager.description || '');
      setContent(onePager.content);
      setIsTemplate(onePager.is_template || false);
    } catch (error) {
      console.error('Failed to load one-pager:', error);
      alert('Failed to load one-pager');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Strip HTML tags to check if there's actual content
    const contentText = content.replace(/<[^>]*>/g, '').trim();
    if (!title.trim() || !contentText) {
      alert('Please provide both title and content');
      return;
    }

    try {
      setSaving(true);

      const onePagerData = {
        title: title.trim(),
        description: description.trim(),
        content: content.trim(),
        spec: {
          sections: [
            {
              title: 'Content',
              content: content.trim()
            }
          ],
          metadata: {
            created: new Date().toISOString(),
            version: '1.0'
          }
        },
        is_template: isTemplate
      };

      if (isEditMode && id) {
        await apiService.updateOnePager(id, onePagerData);
        alert('One-pager updated successfully!');
      } else {
        // Generate a 8-char alphanumeric ID
        const generateId = () => {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
          let result = '';
          for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return result;
        };
        const newId = generateId();
        const newOnePager = await apiService.createOnePager({ ...onePagerData, id: newId });

        // Refresh the main list
        if (onRefresh) {
          onRefresh();
        }

        alert('One-pager created successfully!');
        navigate(`/o/${newOnePager.id}`);
      }
    } catch (error) {
      console.error('Failed to save one-pager:', error);
      alert('Failed to save one-pager. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading one-pager...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-8 border">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="border-gray-200 hover:border-gray-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || !title.trim() || !content.replace(/<[^>]*>/g, '').trim()}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Create'}
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          <Card className="border-gray-200 pt-4">
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-normal text-gray-700">
                  Title *
                </Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter one-pager title"
                  className="h-10 border-gray-200 focus:border-gray-900 focus:ring-0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-normal text-gray-700">
                  Description
                </Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the one-pager"
                  className="h-10 border-gray-200 focus:border-gray-900 focus:ring-0"
                />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Editor
              content={content}
              onUpdate={setContent}
              placeholder="Start writing your one-pager content here..."
            />
            <p className="text-xs text-gray-500">
              Use the toolbar above to format your text with headings, bold, italic, lists, blockquotes, and more.
            </p>
          </div>
        </div>

        {/* Save section */}
        <div className="mt-8">
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-gray-200 hover:border-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !title.trim() || !content.replace(/<[^>]*>/g, '').trim()}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : (isEditMode ? 'Update One-Pager' : 'Create')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};